import type { MatchDetailDto } from '@/api/backoffice-schemas';
import { GameStatusIcon } from './GameStatusIcon';
import { MatchMetadata } from './MatchMetadata';
import { TeamDisplay } from './TeamDisplay';

interface MatchListItemProps {
  data: MatchDetailDto;
}

export const MatchListItem = ({ data }: MatchListItemProps) => {
  if (!data) {
    return null;
  }

  const {
    beginAt,
    numberOfGames,
    matchType,
    status,
    videoGame,
    league,
    teamMatches,
  } = data;

  const team1 = teamMatches?.[0]?.team;
  const team2 = teamMatches?.[1]?.team;

  // Since score is not in TeamMatchDetailDto, we'll need to handle it differently
  // You might need to adjust this based on where the score actually comes from in your API
  const team1Score = teamMatches?.[0]?.score ?? 0;
  const team2Score = teamMatches?.[1]?.score ?? 0;

  if (!(team1 || team2)) {
    return (
      <div className="flex flex-col gap-1 rounded-lg bg-card-bg p-3 hover:bg-light-blue-o15">
        <p className="font-medium text-sm leading-[18px]">No teams available</p>
      </div>
    );
  }

  const getMatchTypeLabel = () => {
    const typeMap: Record<string, string> = {
      best_of: 'Best of',
      first_to: 'First to',
      red_bull_home_ground: 'Redbull best of',
    };

    return `${typeMap[matchType || ''] || ''} ${numberOfGames || ''}`;
  };

  return (
    <div className="flex flex-row items-center justify-between gap-2 rounded-lg bg-card-bg p-3 md:gap-6">
      <GameStatusIcon status={status} videoGame={videoGame} />

      <div className="flex w-[9.813rem] flex-1 flex-col gap-2 md:w-[424px] md:flex-row">
        <TeamDisplay isWinner={Boolean(team1Score > team2Score)} team={team1} />
        <div className="hidden md:block">
          <MatchMetadata
            beginAt={beginAt}
            status={status}
            team1Score={team1Score}
            team2Score={team2Score}
          />
        </div>
        <TeamDisplay
          isSecondary
          isWinner={Boolean(team2Score > team1Score)}
          team={team2}
        />
      </div>

      <div className="flex h-full w-[82px] flex-col items-start justify-around gap-1">
        <p className="font-semibold text-[11px] text-gray leading-3">
          {getMatchTypeLabel()}
        </p>
        <p className="font-normal text-[10px] text-gray leading-3">
          {league?.name}
        </p>
      </div>

      <div className="md:hidden">
        <MatchMetadata
          beginAt={beginAt}
          status={status}
          team1Score={team1Score}
          team2Score={team2Score}
        />
      </div>
    </div>
  );
};
